import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { quoteAPI } from '@/lib/api';
import { ArrowLeft, Ship, Plane, HelpCircle } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const QuoteForm = () => {
  const navigate = useNavigate();
  const [quoteType, setQuoteType] = useState('CIF');
  const [loading, setLoading] = useState(false);
  const { register, handleSubmit, formState: { errors } } = useForm();

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const quoteData = {
        ...data,
        quote_type: quoteType,
        weight_kg: parseFloat(data.weight_kg),
        volume_cbm: parseFloat(data.volume_cbm),
        cargo_value_usd: data.cargo_value_usd ? parseFloat(data.cargo_value_usd) : null,
        hs_code: quoteType === 'CIF' ? data.hs_code : null  // Only include HS code for sea freight
      };

      // Get carrier rates first (no quote created yet)
      const response = await quoteAPI.getRates(quoteData);
      
      // Navigate to carrier comparison page
      navigate('/quote/compare', {
        state: {
          carriers: response.data.carrier_options,
          quoteData: response.data.quote_data,
          creditsRequired: response.data.credits_required,
          hsCodeInfo: response.data.hs_code_info  // Include HS code info
        }
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to fetch rates');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface py-8">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-4xl">
        <Button
          data-testid="back-btn"
          variant="ghost"
          onClick={() => navigate('/buyer/dashboard')}
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>

        <Card className="p-8">
          <h1 className="text-3xl font-chivo font-bold text-slate-900 mb-2">New Quote Request</h1>
          <p className="text-slate-600 mb-8">Fill out the form to generate your shipping quote</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            <Card
              data-testid="quote-type-cif"
              className={`p-6 cursor-pointer border-2 transition-all ${
                quoteType === 'CIF'
                  ? 'border-slate-900 bg-slate-50'
                  : 'border-slate-200 hover:border-slate-300'
              }`}
              onClick={() => setQuoteType('CIF')}
            >
              <Ship className="w-8 h-8 text-slate-900 mb-3" />
              <h3 className="text-lg font-chivo font-semibold text-slate-900 mb-1">CIF Quote</h3>
              <p className="text-sm text-slate-600 mb-2">Sea freight with insurance</p>
              <p className="text-xs text-accent font-medium">10 credits</p>
            </Card>

            <Card
              data-testid="quote-type-airfreight"
              className={`p-6 cursor-pointer border-2 transition-all ${
                quoteType === 'airfreight'
                  ? 'border-slate-900 bg-slate-50'
                  : 'border-slate-200 hover:border-slate-300'
              }`}
              onClick={() => setQuoteType('airfreight')}
            >
              <Plane className="w-8 h-8 text-slate-900 mb-3" />
              <h3 className="text-lg font-chivo font-semibold text-slate-900 mb-1">Airfreight Quote</h3>
              <p className="text-sm text-slate-600 mb-2">Express air delivery</p>
              <p className="text-xs text-accent font-medium">15 credits</p>
            </Card>
          </div>

          <form data-testid="quote-form" onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="origin_port">Origin Port *</Label>
                <Input
                  data-testid="origin-port-input"
                  id="origin_port"
                  placeholder="e.g., Shanghai, China"
                  {...register('origin_port', { required: true })}
                />
                {errors.origin_port && (
                  <span className="text-xs text-red-600">This field is required</span>
                )}
              </div>

              <div>
                <Label htmlFor="destination_port">Destination Port *</Label>
                <Input
                  data-testid="destination-port-input"
                  id="destination_port"
                  placeholder="e.g., Los Angeles, USA"
                  {...register('destination_port', { required: true })}
                />
                {errors.destination_port && (
                  <span className="text-xs text-red-600">This field is required</span>
                )}
              </div>

              <div className="md:col-span-2">
                <Label htmlFor="cargo_description">Cargo Description *</Label>
                <Input
                  data-testid="cargo-description-input"
                  id="cargo_description"
                  placeholder="e.g., Electronics, Machinery"
                  {...register('cargo_description', { required: true })}
                />
                {errors.cargo_description && (
                  <span className="text-xs text-red-600">This field is required</span>
                )}
              </div>

              <div>
                <Label htmlFor="weight_kg">Weight (kg) *</Label>
                <Input
                  data-testid="weight-input"
                  id="weight_kg"
                  type="number"
                  step="0.01"
                  placeholder="1000"
                  {...register('weight_kg', { required: true, min: 0.01 })}
                />
                {errors.weight_kg && (
                  <span className="text-xs text-red-600">Enter a valid weight</span>
                )}
              </div>

              <div>
                <Label htmlFor="volume_cbm">Volume (CBM) *</Label>
                <Input
                  data-testid="volume-input"
                  id="volume_cbm"
                  type="number"
                  step="0.01"
                  placeholder="5.5"
                  {...register('volume_cbm', { required: true, min: 0.01 })}
                />
                {errors.volume_cbm && (
                  <span className="text-xs text-red-600">Enter a valid volume</span>
                )}
              </div>

              <div>
                <Label htmlFor="incoterms">Incoterms *</Label>
                <Input
                  data-testid="incoterms-input"
                  id="incoterms"
                  placeholder="e.g., FOB, CIF, EXW"
                  {...register('incoterms', { required: true })}
                />
                {errors.incoterms && (
                  <span className="text-xs text-red-600">This field is required</span>
                )}
              </div>

              {/* HS Code - Only for Sea Freight (CIF) */}
              {quoteType === 'CIF' && (
                <div>
                  <div className="flex items-center gap-2">
                    <Label htmlFor="hs_code">HS Code (Harmonized System)</Label>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <HelpCircle className="w-4 h-4 text-slate-400 cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          <p>The HS Code is required for customs clearance on sea freight. Find your code at <a href="https://hts.usitc.gov/" target="_blank" rel="noopener noreferrer" className="text-ocean-turquoise underline">hts.usitc.gov</a></p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <Input
                    data-testid="hs-code-input"
                    id="hs_code"
                    placeholder="e.g., 8471.30 (Computers)"
                    {...register('hs_code')}
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Optional but recommended for accurate customs quotes
                  </p>
                </div>
              )}

              <div>
                <Label htmlFor="cargo_value_usd">Cargo Value (USD, Optional)</Label>
                <Input
                  data-testid="cargo-value-input"
                  id="cargo_value_usd"
                  type="number"
                  step="0.01"
                  placeholder="10000"
                  {...register('cargo_value_usd')}
                />
              </div>
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-sm p-4">
              <h4 className="font-inter font-medium text-slate-900 mb-2">Quote Summary</h4>
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Quote Type:</span>
                <span className="font-medium text-slate-900">{quoteType === 'CIF' ? 'CIF (Sea Freight)' : 'Airfreight'}</span>
              </div>
              <div className="flex justify-between text-sm mt-2">
                <span className="text-slate-600">Carrier Network:</span>
                <span className="font-medium text-slate-900">
                  {quoteType === 'CIF' ? 'FreightAmigo' : 'Shippo Express'}
                </span>
              </div>
              <div className="flex justify-between text-sm mt-2">
                <span className="text-slate-600">Credits Required:</span>
                <span className="font-medium text-accent">{quoteType === 'CIF' ? '10' : '15'} credits</span>
              </div>
              {quoteType === 'CIF' && (
                <p className="text-xs text-slate-500 mt-3 border-t pt-3">
                  💡 Sea freight includes customs clearance. HS Code recommended for accurate duty calculations.
                </p>
              )}
            </div>

            <Button
              data-testid="generate-quote-btn"
              type="submit"
              className="w-full"
              disabled={loading}
            >
              {loading ? 'Fetching Rates...' : 'Compare Carriers & Rates'}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default QuoteForm;